import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Bell, BellOff, CheckCircle2, Info, Smartphone, Calendar as CalendarIcon, Link2, Link2Off, ChevronDown, ChevronUp, Settings2, Volume2 } from "lucide-react";
import { getReminderSettings, setReminderSettings, requestNotifPermission } from "../lib/reminders";
import { subscribeToPush, unsubscribeFromPush, getPushStatus, sendTestPush, getActiveSubscription, resyncSubscription } from "../lib/pwa";
import { google } from "../lib/api";
import { getGlobalAlarm, setGlobalAlarm, playAlarm, stopAlarm, TONE_LIST } from "../lib/alarm";
import { toast } from "sonner";

/**
 * Tool-specific settings for the Prescription Manager:
 * Browser notifications, background push, Google Calendar, in-app reminder polling.
 * Collapsible so it doesn't dominate the page.
 */
export default function PrescriptionSettingsPanel() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(getReminderSettings());
  const [alarm, setAlarm] = useState(getGlobalAlarm());
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [pushStatus, setPushStatus] = useState({ supported: false, subscribed: false });
  const [busy, setBusy] = useState(false);
  const [gStatus, setGStatus] = useState({ configured: false, connected: false, email: null });
  const [params, setParams] = useSearchParams();
  const [showUnblockHelp, setShowUnblockHelp] = useState(false);
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent || "") && !window.MSStream;
  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

  useEffect(() => { setReminderSettings(settings); }, [settings]);
  useEffect(() => { setGlobalAlarm(alarm); }, [alarm]);

  useEffect(() => {
    getPushStatus().then(setPushStatus);
    google.status().then(setGStatus).catch(() => {});
  }, []);

  useEffect(() => {
    const g = params.get("google");
    if (g === "connected") {
      toast.success("Google Calendar connected");
      google.status().then(setGStatus).catch(() => {});
      params.delete("google");
      setParams(params, { replace: true });
      setOpen(true);
    } else if (g === "error") {
      toast.error("Google Calendar connection failed: " + (params.get("reason") || "unknown"));
      params.delete("google"); params.delete("reason");
      setParams(params, { replace: true });
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function askPerm() {
    if (perm === "denied") {
      // The browser will refuse to re-prompt — guide the user to unblock.
      setShowUnblockHelp(true);
      return;
    }
    const p = await requestNotifPermission();
    setPerm(p);
    if (p === "granted") toast.success("Notifications enabled");
    else if (p === "denied") {
      toast.error("Notifications blocked");
      setShowUnblockHelp(true);
    }
  }

  async function toggleBackgroundPush(on) {
    setBusy(true);
    try {
      if (on) {
        if (perm === "denied") {
          // Browser will not re-prompt — surface the unblock guide.
          setShowUnblockHelp(true);
          toast.error("Notifications were blocked earlier", {
            description: "Tap “How to unblock” for step-by-step instructions.",
          });
          return;
        }
        if (perm !== "granted") {
          const p = await requestNotifPermission();
          setPerm(p);
          if (p === "denied") {
            setShowUnblockHelp(true);
            toast.error("Notifications were blocked", {
              description: "Tap “How to unblock” to allow them.",
            });
            return;
          }
          if (p !== "granted") { toast.error("Notifications permission required"); return; }
        }
        const res = await subscribeToPush();
        if (res.ok) { toast.success("Background push enabled"); }
        else { toast.error(`Unsupported: ${res.reason}`); }
      } else {
        await unsubscribeFromPush();
        toast.success("Background push disabled");
      }
      setPushStatus(await getPushStatus());
    } catch (e) {
      toast.error("Could not update push subscription");
    } finally {
      setBusy(false);
    }
  }

  async function testPush() {
    try {
      const res = await sendTestPush();
      // New diagnostic-rich response shape: {sent, subs, errors[], client_id}
      if (res.sent > 0) {
        toast.success("Test push sent — check your notifications");
        return;
      }
      // Try a self-heal then retry once before showing an error
      const sub = await getActiveSubscription();
      if (sub) {
        await resyncSubscription(sub);
        const res2 = await sendTestPush();
        if (res2.sent > 0) {
          toast.success("Re-synced and sent — check your notifications");
          return;
        }
        const why = (res2.errors && res2.errors[0]) || (res.errors && res.errors[0]) || "unknown";
        toast.error("Push delivery failed", {
          description: `Reason: ${why}. Try toggling Background Push off then on again.`,
        });
      } else {
        toast.warning("No browser subscription found", {
          description: "Toggle Background Push off then on to re-subscribe.",
        });
      }
    } catch (e) {
      toast.error("Test failed", { description: String(e?.message || e).slice(0, 120) });
    }
  }

  async function connectGoogle() {
    try { const { url } = await google.authUrl(); window.location.href = url; }
    catch { toast.error("Could not start Google connect"); }
  }
  async function disconnectGoogle() {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try { await google.disconnect(); toast.success("Disconnected"); setGStatus(await google.status()); }
    catch { toast.error("Disconnect failed"); }
  }

  const summary = [
    perm === "granted" ? "Notifications on" : "Notifications off",
    pushStatus.subscribed ? "Push on" : "Push off",
    gStatus.connected ? "Google linked" : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="card-tactile rounded-2xl mb-6" data-testid="rx-settings-panel">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left"
        data-testid="rx-settings-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "color-mix(in srgb, var(--primary) 12%, transparent)" }}>
            <Settings2 className="h-5 w-5" style={{ color: "var(--primary)" }} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold tracking-tight">Reminders & Calendar</div>
            <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{summary || "Configure reminders for your prescriptions"}</div>
          </div>
        </div>
        {open ? <ChevronUp className="h-5 w-5 shrink-0" /> : <ChevronDown className="h-5 w-5 shrink-0" />}
      </button>

      {open && (
        <div className="border-t p-5 space-y-5" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                  {perm === "granted" ? <Bell className="h-4 w-4 text-[color:var(--primary-ink)]" /> : <BellOff className="h-4 w-4 text-[color:var(--text-muted)]" />}
                  Browser Notifications
                </h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Shown by your OS. Status: <span className="font-semibold">{perm}</span></p>
              </div>
              {perm === "granted" && <span className="dose-pill taken"><CheckCircle2 className="h-3 w-3" /> Enabled</span>}
              {perm === "default" && (
                <Button onClick={askPerm} data-testid="enable-notifications" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">Enable</Button>
              )}
              {perm === "denied" && (
                <Button
                  onClick={() => setShowUnblockHelp(v => !v)}
                  data-testid="how-to-unblock"
                  variant="outline" size="sm"
                >
                  How to unblock
                </Button>
              )}
            </div>
            {perm === "denied" && showUnblockHelp && (
              <div
                data-testid="unblock-instructions"
                className="mt-3 rounded-lg p-3 border-l-4 text-sm"
                style={{
                  borderLeftColor: "var(--accent)",
                  background: "color-mix(in srgb, var(--accent) 8%, transparent)",
                  color: "var(--text-main)",
                }}
              >
                <p className="font-semibold mb-2">Notifications were blocked earlier. Browsers won't re-prompt — you'll need to allow them manually:</p>
                {isAndroid ? (
                  <ol className="list-decimal pl-5 space-y-1.5">
                    <li>Tap the <b>three-dot menu (⋮)</b> in Chrome's top-right.</li>
                    <li>Open <b>Settings</b> → <b>Site settings</b> → <b>Notifications</b>.</li>
                    <li>Find <b>verihandi.com</b> in the <b>Blocked</b> list and tap it.</li>
                    <li>Tap <b>Allow</b>, then return here and toggle Background Push on.</li>
                  </ol>
                ) : isIOS ? (
                  <ol className="list-decimal pl-5 space-y-1.5">
                    <li>Open the iOS <b>Settings</b> app.</li>
                    <li>Scroll down to <b>VeriHandi</b> (the installed PWA).</li>
                    <li>Tap <b>Notifications</b> and turn on <b>Allow Notifications</b>.</li>
                    <li>Return here and toggle Background Push on.</li>
                  </ol>
                ) : (
                  <ol className="list-decimal pl-5 space-y-1.5">
                    <li>Click the <b>lock icon</b> in your browser's address bar.</li>
                    <li>Find <b>Notifications</b> in the dropdown and set it to <b>Allow</b>.</li>
                    <li>Reload the page and toggle Background Push on.</li>
                  </ol>
                )}
              </div>
            )}
          </div>

          <div className="pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-[color:var(--primary-ink)]" /> Background Push
                </h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Reminders arrive even when VeriHandi is closed. Works on desktop and installed PWAs (iOS 16.4+).</p>
              </div>
              <Switch checked={pushStatus.subscribed} onCheckedChange={toggleBackgroundPush} disabled={busy || !pushStatus.supported} data-testid="push-toggle" />
            </div>
            {pushStatus.subscribed && (
              <div className="mt-3">
                <Button onClick={testPush} variant="outline" size="sm" data-testid="push-test">
                  Send test push
                </Button>
              </div>
            )}
            <div className="mt-3 flex items-start gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              {isStandalone
                ? <span>Running as installed PWA — background push works reliably.</span>
                : isIOS
                  ? <span>On iPhone, tap <b>Share</b> → <b>"Add to Home Screen"</b>. Open VeriHandi from the home screen, then enable Background Push here (iOS 16.4+).</span>
                  : <span>Toggle on to receive reminders even when VeriHandi is closed. Works in Chrome, Edge, Firefox and Samsung Internet on Android.</span>}
            </div>
            {isStandalone && isAndroid && (
              <div
                className="mt-3 rounded-lg p-2.5 text-[11px] flex items-start gap-2"
                style={{
                  background: "color-mix(in srgb, var(--warning) 8%, transparent)",
                  color: "var(--text-main)",
                }}
              >
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "var(--warning)" }} />
                <span>
                  <b>Tip:</b> If you've added VeriHandi to your home screen more than once, each shortcut has its own data. Pin only one shortcut and remove the others to keep prescriptions in sync.
                </span>
              </div>
            )}
          </div>

          {gStatus.configured && (
            <div className="pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }} data-testid="google-section">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-[color:var(--primary-ink)]" /> Google Calendar
                  </h3>
                  <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                    {gStatus.connected ? <>Connected{gStatus.email ? ` as ${gStatus.email}` : ""}. Push any schedule to your calendar in one click.</> : "Connect to push full dose schedules into Google Calendar with 30min + 60min reminders."}
                  </p>
                </div>
                {gStatus.connected ? (
                  <Button onClick={disconnectGoogle} variant="outline" className="border-[color:var(--border-subtle)]" data-testid="google-disconnect">
                    <Link2Off className="h-4 w-4 mr-2" /> Disconnect
                  </Button>
                ) : (
                  <Button onClick={connectGoogle} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="google-connect">
                    <Link2 className="h-4 w-4 mr-2" /> Connect
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-base tracking-tight">In-app reminder polling</h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Popup + OS notification while VeriHandi is open.</p>
              </div>
              <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="reminder-enabled" />
            </div>
            <div className="mt-3">
              <label className="label-caps">Lead time (minutes)</label>
              <Input
                type="number" min="1" max="120"
                data-testid="reminder-lead"
                value={settings.leadMinutes}
                onChange={(e) => setSettings({ ...settings, leadMinutes: Math.max(1, Math.min(120, Number(e.target.value))) })}
                className="mt-1 max-w-[140px]"
              />
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Calendar exports (.ics / Google) always include 30min and 1hr reminders.</p>
            </div>
          </div>

          <div className="pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-[color:var(--primary-ink)]" /> Alarm ring (global default)
                </h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Plays a tone + optional vibration when a dose reminder pops up. Overridable per-prescription on the Rx detail page.</p>
              </div>
              <Switch checked={alarm.enabled} onCheckedChange={(v) => setAlarm({ ...alarm, enabled: v })} data-testid="alarm-enabled" />
            </div>
            {alarm.enabled && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="label-caps">Tone</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {TONE_LIST.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setAlarm({ ...alarm, tone: t.id })}
                        data-testid={`alarm-tone-${t.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${alarm.tone === t.id ? "bg-[color:var(--primary)] text-white border-transparent" : "bg-transparent text-[color:var(--text-main)] border-[color:var(--border-subtle)] hover:border-[color:var(--primary)]"}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label-caps">Volume</label>
                  <input
                    type="range" min="0" max="1" step="0.05"
                    value={alarm.volume}
                    onChange={(e) => setAlarm({ ...alarm, volume: Number(e.target.value) })}
                    data-testid="alarm-volume"
                    className="mt-1 w-full accent-[color:var(--primary)]"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label htmlFor="alarm-vibrate" className="text-sm">Vibrate (Android / installed PWA)</label>
                  <Switch
                    id="alarm-vibrate"
                    checked={alarm.vibrate}
                    onCheckedChange={(v) => setAlarm({ ...alarm, vibrate: v })}
                    data-testid="alarm-vibrate"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label htmlFor="alarm-ring-on-open" className="text-sm">Ring when opened from a push notification</label>
                  <Switch
                    id="alarm-ring-on-open"
                    checked={alarm.ringOnOpen}
                    onCheckedChange={(v) => setAlarm({ ...alarm, ringOnOpen: v })}
                    data-testid="alarm-ring-on-open"
                  />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    onClick={() => playAlarm(null, { preview: true })}
                    variant="outline"
                    size="sm"
                    data-testid="alarm-preview"
                    className="border-[color:var(--border-subtle)]"
                  >
                    <Volume2 className="h-3 w-3 mr-1" /> Preview
                  </Button>
                  <Button onClick={stopAlarm} variant="ghost" size="sm" className="text-[color:var(--text-muted)]">Stop</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
